import React from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogContent,
  Tooltip,
  TextField,
  Checkbox,
  InputAdornment,
  DialogTitle,
  DialogActions,
  MenuItem,
  TablePagination,
  Snackbar,
  Alert,
  useMediaQuery,
  type SxProps,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCode from 'react-qr-code';
import {
  useProjectStore
} from '../store/projectStore';
import {
  useLineStore
} from '../store/lineStore';
import {
  useIssueStore
} from '../store/issueStore';
import {
  ProjectFormDialog
} from '../components/ProjectFormDialog';
import {
  IssuesStatisticsChart
} from '../components/IssuesStatisticsChart';
import {
  format
} from 'date-fns';
import {
  Breadcrumbs
} from '../components/Breadcrumbs';
import {
  getImageUrl,
  loadImageWithFallback
} from '../utils/imageUrl';
import ExcelJS from 'exceljs';
import { API_BASE_URL } from '../api/axios';
import { useTheme } from '@mui/material/styles';

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const {
    currentProject,
    isLoading: projectLoading,
    error: projectError,
    fetchProject,
    updateProject,
  } = useProjectStore();

  const {
    lines,
    isLoading: linesLoading,
    error: linesError,
    fetchLines,
  } = useLineStore();

  const {
    issues,
    loading: issuesLoading,
    error: issuesError,
    fetchIssues,
    deleteIssue,
    clearIssues,
  } = useIssueStore();
  
  // Track current project ID to detect changes
  const [currentProjectId, setCurrentProjectId] = React.useState<string | undefined>(id);

  const [projectFormOpen, setProjectFormOpen] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState<string | null>(null);
  const [searchInput, setSearchInput] = React.useState('');
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedIssues, setSelectedIssues] = React.useState<number[]>([]);
  const [selectAll, setSelectAll] = React.useState(false);
  const [showFilters, setShowFilters] = React.useState(false);
  const [filterApplied, setFilterApplied] = React.useState(false);
  const [shareDialogOpen, setShareDialogOpen] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // Row number filter
  const [fromRowNumber, setFromRowNumber] = React.useState<string>('');
  const [toRowNumber, setToRowNumber] = React.useState<string>('');
  const [statusFilter, setStatusFilter] = React.useState<string>('');
  const [rankFilter, setRankFilter] = React.useState<string>('');
  const [issueTypeFilter, setIssueTypeFilter] = React.useState<string[]>([]);
  const [lineFilter, setLineFilter] = React.useState<string[]>([]);
  const [issuedByFilter, setIssuedByFilter] = React.useState<string[]>([]);
  const [cmPicFilter, setCmPicFilter] = React.useState<string[]>([]);
  const [approvalFilter, setApprovalFilter] = React.useState<string[]>([]);

  type Order = 'asc' | 'desc';
  type OrderBy = 'id' | 'type' | 'rank' | 'completion_date' | 'status' | 'progress';

  const cellBorder = {
    border: '1px solid #d0d0d0',
    backgroundClip: 'padding-box',
  };

  const stickyBaseLefts = {
    no: 0,
    issueId: 60,
    issueType: 160,
    rank: 280,
    line: 360,
    part: 460,
    problem: 610,
  } as const;

  const stickyColumns = React.useMemo<(keyof typeof stickyBaseLefts)[]>(
    () =>
      isLargeScreen
        ? ['no', 'issueId', 'issueType', 'rank', 'line', 'part', 'problem']
        : ['no', 'issueId'],
    [isLargeScreen]
  );
  const lastStickyKey = stickyColumns[stickyColumns.length - 1];

  const getStickySx = React.useCallback(
    (
      key: keyof typeof stickyBaseLefts,
      options: {
        header?: boolean;
        width: number;
        minWidth?: number;
        highlightDivider?: boolean;
      }
    ) => {
      const baseStyle = {
        ...cellBorder,
        width: `${options.width}px`,
        minWidth: `${options.minWidth ?? options.width}px`,
        overflow: 'hidden',
      };

      if (!stickyColumns.includes(key)) {
        return baseStyle;
      }

      const leftOffset = stickyBaseLefts[key] + (showFilters ? 50 : 0);

      return {
        ...baseStyle,
        position: 'sticky',
        left: `${leftOffset}px`,
        zIndex: options.header ? 30 : 15,
        backgroundColor: options.header ? '#e0e0e0' : 'white',
        boxShadow: options.header ? 'none' : '2px 0 4px -2px rgba(0,0,0,0.1)',
        borderRight: options.highlightDivider
          ? '3px solid #d0d0d0'
          : key === lastStickyKey
          ? '2px solid rgba(0,0,0,0.1)'
          : '1px solid #d0d0d0',
      };
    },
    [cellBorder, showFilters, stickyColumns, lastStickyKey]
  );
  
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<OrderBy>('id');

  type ExcelImageExtension = 'jpeg' | 'png' | 'gif';
  const apiRootUrl = React.useMemo(() => API_BASE_URL.replace(/\/api\/v1\/?$/, ''), []);

  const ensureSecureImageUrl = (url: string) => {
    if (url?.startsWith('http://') && window.location.protocol === 'https:') {
      try {
        const parsed = new URL(url);
        parsed.protocol = 'https:';
        return parsed.toString();
      } catch (error) {
        console.warn('Failed to normalize image URL protocol', url, error);
      }
    }
    return url;
  };

  const getAuthHeaders = (): HeadersInit | undefined => {
    const token = localStorage.getItem('token');
    if (token) {
      return { Authorization: token };
    }
    return undefined;
  };

  const fetchPhotoAsBase64 = async (fileName: string) => {
    try {
      const downloadUrl = `${apiRootUrl}/api/v1/issues/photos/download/${encodeURIComponent(fileName)}`;
      const resizedBase64 = await fetchAndResizeImage(downloadUrl, 120);
      const [, base64Payload] = resizedBase64.split(',');
      return {
        base64: base64Payload || resizedBase64,
        extension: 'png' as ExcelImageExtension,
      };
    } catch (error) {
      console.warn(`Skipping photo ${fileName} for export due to error:`, error);
      return null;
    }
  };

  const fetchAndResizeImage = async (imageUrl: string, maxDimension: number): Promise<string> => {
    const response = await fetch(imageUrl, {
      mode: 'cors',
      credentials: 'include',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }
    const blob = await response.blob();
    return resizeImageBlob(blob, maxDimension);
  };

  const resizeImageBlob = (blob: Blob, maxDimension: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(blob);
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to create canvas context'));
            return;
          }
          const scale = Math.min(maxDimension / img.width, maxDimension / img.height, 1);
          const width = Math.max(img.width * scale, 1);
          const height = Math.max(img.height * scale, 1);
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/png');
          URL.revokeObjectURL(objectUrl);
          resolve(dataUrl);
        } catch (err) {
          URL.revokeObjectURL(objectUrl);
          reject(err);
        }
      };
      img.onerror = (event) => {
        URL.revokeObjectURL(objectUrl);
        reject(event as unknown as Error);
      };
      img.src = objectUrl;
    });
  };

  const createAnchorPosition = (col: number, row: number) => ({
    col,
    row,
  });

  // Handle pagination
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const backendStatusFilter = React.useMemo(() => {
    switch (statusFilter) {
      case '25':
        return 'waiting_response';
      case '50':
        return 'inprogress';
      case '75':
        return 'waiting_approval';
      case '100':
        return 'completed';
      default:
        return undefined;
    }
  }, [statusFilter]);

  const resetFilters = () => {
    setOrderBy('id');
    setOrder('asc');
    setSearchInput('');
    setSearchTerm('');
    setStatusFilter('');
    setRankFilter('');
    setIssueTypeFilter([]);
    setLineFilter([]);
    setIssuedByFilter([]);
    setCmPicFilter([]);
    setApprovalFilter([]);
    setFromRowNumber('');
    setToRowNumber('');
    setSelectedIssues([]);
    setFilterApplied(false);
    setPage(0);
  };

  // Filter issues based on row number range and selection filter
  const filteredIssues = React.useMemo(() => {
    if (!issues) return [];
    
    let result = issues;

    // Status filter by percentage buckets
    if (statusFilter) {
      const targetPercent = parseInt(statusFilter, 10);
      result = result.filter(
        (issue) => Math.round(getStatusPercentage(issue.status) * 100) === targetPercent
      );
    }

    // Rank filter
    if (rankFilter) {
      const targetRank = rankFilter.toLowerCase();
      result = result.filter((issue) => issue.rank?.toLowerCase() === targetRank);
    }

    // Issue type multi-select
    if (issueTypeFilter.length) {
      const set = new Set(issueTypeFilter);
      result = result.filter((issue) => set.has(issue.type));
    }

    // Line multi-select (by name)
    if (lineFilter.length) {
      const set = new Set(lineFilter);
      result = result.filter((issue) => issue.line_name && set.has(issue.line_name));
    }

    // Issued By multi-select
    if (issuedByFilter.length) {
      const set = new Set(issuedByFilter);
      result = result.filter((issue) => issue.created_by_name && set.has(issue.created_by_name));
    }

    // CM PIC multi-select
    if (cmPicFilter.length) {
      const set = new Set(cmPicFilter);
      result = result.filter((issue) => issue.pic_name && set.has(issue.pic_name));
    }

    // Approval multi-select
    if (approvalFilter.length) {
      const set = new Set(approvalFilter);
      result = result.filter((issue) => issue.approver_pic_name && set.has(issue.approver_pic_name));
    }

    // Apply row number filter first so selection uses the already-sliced set
    if (fromRowNumber || toRowNumber) {
      const from = fromRowNumber ? parseInt(fromRowNumber, 10) : 1;
      const to = toRowNumber ? parseInt(toRowNumber, 10) : result.length;
      
      if (from > 0 && to > 0 && from <= to) {
        result = result.slice(from - 1, to);
      }
    }

    if (filterApplied && selectedIssues.length > 0) {
      const selectedSet = new Set(selectedIssues);
      result = result.filter(issue => selectedSet.has(issue.id));
    }
    
    return result;
  }, [
    issues,
    statusFilter,
    rankFilter,
    issueTypeFilter,
    lineFilter,
    issuedByFilter,
    cmPicFilter,
    approvalFilter,
    fromRowNumber,
    toRowNumber,
    selectedIssues,
    filterApplied,
  ]);

  // Options for multi-select filters, derived from current issues
  const issueTypeOptions = React.useMemo(() => {
    if (!issues) return [];
    return Array.from(new Set(issues.map((i) => i.type).filter(Boolean))).sort();
  }, [issues]);

  const lineOptions = React.useMemo(() => {
    if (!issues) return [];
    return Array.from(new Set(issues.map((i) => i.line_name).filter(Boolean))).sort();
  }, [issues]);

  const issuedByOptions = React.useMemo(() => {
    if (!issues) return [];
    return Array.from(new Set(issues.map((i) => i.created_by_name).filter(Boolean))).sort();
  }, [issues]);

  const cmPicOptions = React.useMemo(() => {
    if (!issues) return [];
    return Array.from(new Set(issues.map((i) => i.pic_name).filter(Boolean))).sort();
  }, [issues]);

  const approvalOptions = React.useMemo(() => {
    if (!issues) return [];
    return Array.from(new Set(issues.map((i) => i.approver_pic_name).filter(Boolean))).sort();
  }, [issues]);

  // Pagination slice
  const paginatedIssues = React.useMemo(() => {
    return filteredIssues.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredIssues, page, rowsPerPage]);

  // Handle select all checkbox
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectAll(event.target.checked);
    setFilterApplied(false); // wait for explicit apply
    if (event.target.checked) {
      setSelectedIssues(filteredIssues.map(issue => issue.id));
    } else {
      setSelectedIssues([]);
    }
  };

  // Handle individual row selection
  const handleSelectRow = (issueId: number, event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setFilterApplied(false); // wait for explicit apply after selection changes
    setSelectedIssues(prev => {
      if (prev.includes(issueId)) {
        return prev.filter(id => id !== issueId);
      } else {
        return [...prev, issueId];
      }
    });
  };

  // Check if all filtered issues are selected
  React.useEffect(() => {
    if (filteredIssues.length > 0 && selectedIssues.length === filteredIssues.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedIssues, filteredIssues]);

  // Function to export project details and BIRA summary to Excel with images
  const exportToExcelWithExcelJS = async () => {
    if (!currentProject || !issues) return;

    try {
      const workbook = new ExcelJS.Workbook();
      workbook.created = new Date();
      workbook.modified = new Date();

      // Project Details worksheet
      const projectSheet = workbook.addWorksheet('Project Details', {
        properties: { defaultRowHeight: 18 },
      });
      projectSheet.addRows([
        ['Project Details'],
        ['Name', currentProject.name],
        ['Code', currentProject.code || ''],
        ['Lines Count', currentProject.lines_count],
        ['Issues Count', currentProject.issues_count],
        ['Created On', `${format(new Date(currentProject.created_on), 'PPP')} ${format(new Date(currentProject.created_on), 'p')}`],
      ]);
      projectSheet.getRow(1).font = { bold: true };
      projectSheet.columns = [
        { key: 'label', width: 18 },
        { key: 'value', width: 40 },
      ];

      // BIRA summary worksheet definition
      const biraSheet = workbook.addWorksheet('BIRA Summary', {
        views: [{ state: 'frozen', ySplit: 1 }],
      });
      const biraColumns = [
        { header: 'No', key: 'no', width: 6 },
        { header: 'Issue ID', key: 'issueId', width: 14 },
        { header: 'Type of Issue', key: 'type', width: 18 },
        { header: 'Rank', key: 'rank', width: 8 },
        { header: 'Line', key: 'line', width: 15 },
        { header: 'Part No', key: 'partNo', width: 15 },
        { header: 'Part Name', key: 'partName', width: 20 },
        { header: 'Problem Description', key: 'problem', width: 40 },
        { header: 'Picture 1', key: 'picture1', width: 18 },
        { header: 'Picture 2', key: 'picture2', width: 18 },
        { header: 'Counter Measure Request', key: 'counterMeasure', width: 28 },
        { header: 'Final Counter Measure', key: 'finalCounterMeasure', width: 28 },
        { header: 'Issuer', key: 'issuer', width: 18 },
        { header: 'Issuer Date', key: 'issuerDate', width: 16 },
        { header: 'PIC', key: 'pic', width: 18 },
        { header: 'Approver', key: 'approver', width: 18 },
        { header: 'Due Date', key: 'dueDate', width: 16 },
        { header: 'Status', key: 'status', width: 14 },
        { header: 'Progress (%)', key: 'progress', width: 14 },
      ];
      biraSheet.columns = biraColumns;
      biraSheet.getRow(1).font = { bold: true };
      biraSheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };

      const pictureColumnStart =
        biraColumns.findIndex((col) => col.key === 'picture1') + 1;
      const pictureRowHeight = 110;
      const picturePadding = 0.15;
      const pictureSize = 85;

      // Filtered issues
      let issuesToExport = issues;
      if (filterApplied && selectedIssues.length > 0) {
        issuesToExport = issues.filter((issue) =>
          selectedIssues.includes(issue.id)
        );
      } else if (searchTerm) {
        issuesToExport = filteredIssues;
      }

      console.log(`Exporting ${issuesToExport.length} issues with images`);

      for (const issue of issuesToExport) {
        const row = biraSheet.addRow({
          no: issue.id,
          issueId: issue.issue_code || `${issue.id}`,
          type: formatWord(issue.type),
          rank: formatWord(issue.rank),
          line: issue.line_name || '',
          partNo: issue.part_number || '',
          partName: issue.part_name || '',
          problem: issue.problem || '',
          picture1: '',
          picture2: '',
          counterMeasure: issue.counter_measure || '',
          finalCounterMeasure: issue.final_counter_measure || '',
          issuer: issue.created_by_name || '',
          issuerDate: issue.created_on ? formatDate(new Date(issue.created_on)) : '',
          pic: issue.pic_name || '',
          approver: issue.approver_pic_name || '',
          dueDate: issue.completion_date ? formatDate(new Date(issue.completion_date)) : '',
          status: formatWord(issue.status),
          progress: `${Math.round(getStatusPercentage(issue.status) * 100)}%`,
        });

        row.alignment = { vertical: 'top', wrapText: true };

        const photos = (issue.photos || []).slice(0, 2);
        if (photos.length > 0) {
          row.height = Math.max(row.height ?? pictureRowHeight, pictureRowHeight);
          for (let photoIndex = 0; photoIndex < photos.length; photoIndex += 1) {
            const photo = photos[photoIndex];
            const photoData = await fetchPhotoAsBase64(photo.file_name);
            if (!photoData) continue;
            const imageId = workbook.addImage({
              base64: photoData.base64,
              extension: photoData.extension,
            });

            const columnIndex = pictureColumnStart + photoIndex;
            const rowIndex = row.number;

            const topLeft = createAnchorPosition(
              columnIndex - 1 + picturePadding,
              rowIndex - 1 + picturePadding
            );
            biraSheet.addImage(imageId, {
              tl: topLeft,
              ext: {
                width: pictureSize,
                height: pictureSize,
              },
              editAs: 'oneCell',
            });
          }
        }
      }

      const projectCode = currentProject.code ? currentProject.code : '';
      const projectName = currentProject.name.replace(/\s+/g, '_');
      const dateTimeStr = format(new Date(), 'yyyyMMdd_HHmmss');
      const fileName = projectCode
        ? `${projectCode}_${projectName}_bira_summary_${dateTimeStr}.xlsx`
        : `${projectName}_bira_summary_${dateTimeStr}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      console.log('Excel export with images completed successfully');

    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export to Excel. Please try again.');
    }
  };

  // Apply/clear selection filter helpers
  const applySelectionFilter = () => {
    setFilterApplied(true);
    setPage(0); // reset pagination so first filtered row stays visible
  };

  const clearAppliedFilter = () => {
    setFilterApplied(false);
  };
  
  // Toggle filter mode
  const toggleFilters = () => {
    setShowFilters(!showFilters);
    if (showFilters) {
      // Clear selections when exiting filter mode
      setSelectedIssues([]);
      setSelectAll(false);
      setFilterApplied(false);
    }
  };

  // Navigate to issue detail
  const navigateToIssueDetail = (issueId: number) => {
    navigate(`/projects/${id}/issues/${issueId}/detail`);
  };

  const commitSearch = React.useCallback(() => {
    const normalized = searchInput.trim();
    setSearchTerm((prev) => (prev === normalized ? prev : normalized));
    if (searchInput !== normalized) {
      setSearchInput(normalized);
    }
  }, [searchInput]);

  React.useEffect(() => {
    if (!id) {
      return;
    }

    if (currentProjectId !== id) {
      clearIssues();
      setCurrentProjectId(id);
      setPage(0);
      setSelectedIssues([]);
      setSelectAll(false);
      setSearchInput('');
      setSearchTerm('');
      setFilterApplied(false);
    }

    const projectId = parseInt(id, 10);
    fetchProject(projectId);
    fetchLines({ project_id: projectId });
  }, [id, currentProjectId, fetchProject, fetchLines, clearIssues]);

  React.useEffect(() => {
    if (!id) {
      return;
    }

    const projectId = parseInt(id, 10);
    const normalizedSearch = searchTerm.trim();
    fetchIssues({
      project_id: projectId,
      status: backendStatusFilter,
      rank: rankFilter || undefined,
      issue_type: issueTypeFilter.length ? issueTypeFilter : undefined,
      line_name: lineFilter.length ? lineFilter : undefined,
      issued_by: issuedByFilter.length ? issuedByFilter : undefined,
      cm_pic: cmPicFilter.length ? cmPicFilter : undefined,
      approval: approvalFilter.length ? approvalFilter : undefined,
      order_by: orderBy,
      order,
      search: normalizedSearch || undefined,
    });
  }, [
    id,
    orderBy,
    order,
    searchTerm,
    backendStatusFilter,
    rankFilter,
    issueTypeFilter,
    lineFilter,
    issuedByFilter,
    cmPicFilter,
    approvalFilter,
    fetchIssues
  ]);

  const handleProjectFormSubmit = async (data: any) => {
    if (currentProject) {
      await updateProject(currentProject.id, data);
      setSuccessMessage('Event updated successfully');
    }
  };

  const handleCreateIssueClick = () => {
    navigate(`/projects/${id}/issues/new`);
  };
  const handleDataAnalysisClick = () => {
    navigate(`/projects/${id}/data-analysis`);
  };
  const handleLinesClick = () => {
    navigate(`/projects/${id}/lines`);
  };

  const handleDetailIssueClick = (issue: any) => {
    navigate(`/projects/${id}/issues/${issue.id}/detail`);
  };

  function getStatusPercentage(status: string) {
    switch (status) {
      case 'waiting_response': return 0.25;
      case 'inprogress': return 0.5;
      case 'waiting_approval': return 0.75;
      case 'completed': return 1;
      default: return 0;
    }
  }

  // Add a new state for image loading status
  const [loadedImages, setLoadedImages] = React.useState<Record<string, string>>({});

  // Add a function to handle image loading with fallback
  const handleImageLoad = async (fileName: string, photoId: number) => {
    try {
      const imageUrl = getImageUrl(fileName);
      const resolvedUrl = await loadImageWithFallback(imageUrl);
      setLoadedImages(prev => ({
        ...prev,
        [`${photoId}`]: resolvedUrl
      }));
    } catch (error) {
      console.error('Error loading image:', error);
      // Use a placeholder in case of error
      setLoadedImages(prev => ({
        ...prev,
        [`${photoId}`]: '/placeholder-image.svg'
      }));
    }
  };

  // Load images when issues change
  React.useEffect(() => {
    const loadIssueImages = async () => {
      if (filteredIssues?.length) {
        for (const issue of filteredIssues) {
          if (issue.photos?.length) {
            for (const photo of issue.photos) {
              await handleImageLoad(photo.file_name, photo.id);
            }
          }
        }
      }
    };
    
    loadIssueImages();
  }, [filteredIssues]);

  // Function to convert image URL to base64 with resizing
  const getResizedImageBase64 = async (imageUrl: string): Promise<string> => {
    try {
      // Create a new Image object
      const img = new Image();
      
      // Create a promise to handle the image loading
      const imageLoaded = new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load image'));
        
        // Set crossOrigin to anonymous to avoid CORS issues when loading images from different domains
        img.crossOrigin = 'anonymous';
      });
      
      // Set the source of the image
      img.src = imageUrl;
      
      // Wait for the image to load
      await imageLoaded;
      
      // Create a canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }
      
      // Set the canvas dimensions to 3cm x 3cm (approximately 113 x 113 pixels at 96 DPI)
      // Excel uses 96 DPI by default
      const size = 113; // 3cm at 96 DPI
      canvas.width = size;
      canvas.height = size;
      
      // Draw the image on the canvas with proper scaling
      ctx.drawImage(img, 0, 0, size, size);
      
      // Convert the canvas to base64 data URL
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Error converting image to base64:', error);
      return ''; // Return empty string if conversion fails
    }
  };

  // Generate the public URL for sharing
  const getPublicUrl = () => {
    return `${window.location.origin}/view/${id}`;
  };

  // Handle copying the URL to clipboard
  const handleCopyUrl = () => {
    try {
      const publicUrl = getPublicUrl();
      navigator.clipboard.writeText(publicUrl)
        .catch((err) => {
          console.error('Failed to copy text: ', err);
          // Fallback method for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = publicUrl;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        });
    } catch (err) {
      console.error('Copy failed: ', err);
    }
  };

  if (projectLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={200} height={40} />
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={200} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={200} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (projectError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{projectError}</Typography>
      </Box>
    );
  }

  if (!currentProject) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Project not found</Typography>
      </Box>
    );
  }

  const formatDate = (date: Date) => {
    const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];

    const day = date.getDate(); 
    const month = months[date.getMonth()]; 
    const year = date.getFullYear(); 

    const formattedDay = day < 10 ? `0${day}` : day;

    return `${formattedDay}-${month}-${year}`;
  }

  const formatWord = (word: string) => {
    return word
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  const highlightMatch = (
    value: string | number | null | undefined,
    query: string
  ): React.ReactNode => {
    const text = value !== undefined && value !== null ? String(value) : '';

    if (!query) return text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerText.indexOf(lowerQuery);

    if (index === -1) return text;

    const before = text.slice(0, index);
    const match = text.slice(index, index + query.length);
    const after = text.slice(index + query.length);

    return (
      <>
        {before}
        <Box component="span" sx={{ fontWeight: 'bold' }}>
          {match}
        </Box>
        {after}
      </>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs 
        items={[
          { label: 'Events', path: '/projects' },
          { label: currentProject.name }
        ]} 
      />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <IconButton onClick={() => navigate('/projects')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
          { currentProject?.code || 'Loading...'}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setShareDialogOpen(true)}
        >
          Share
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Project Details and Statistics Row */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {/* Column 1: Event Details */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header with title */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }} gutterBottom>
                    Event Details
                  </Typography>
                  <Divider />
                </Box>

                {/* Name section */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.3, fontSize: '0.75rem' }}>
                    Name
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '1rem', fontWeight: '500' }}>
                    {currentProject.name || 'No name'}
                  </Typography>
                </Box>

                {/* Stats row: Issues, Lines, Created On */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.3, fontSize: '0.75rem' }}>
                      Issues
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                      {currentProject.issues_count}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.3, fontSize: '0.75rem' }}>
                      Lines
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                      {currentProject.lines_count}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.3, fontSize: '0.75rem' }}>
                      Created On
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      {formatDate(new Date(currentProject.created_on))}
                    </Typography>
                  </Box>
                </Box>

                 {/* Add line devider here */}
                 <Divider />

                {/* Buttons row */}
                <Box sx={{ display: 'flex', gap: 0.5, mt:2 }}>
                  <Button
                    variant="contained"
                    onClick={handleLinesClick}
                    // size="medium"
                    sx={{ fontSize: '1rem', px: 1, py: 0.5, textTransform: 'uppercase', mr: 1 }}
                  >
                    LINES
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleDataAnalysisClick}
                    // size="medium"
                    sx={{ fontSize: '1rem', px: 1, py: 0.5, textTransform: 'uppercase' }}
                  >
                    DATA ANALYSIS
                  </Button>
                </Box>
              </Paper>
            </Grid>

            {/* Column 2: Statistics by Status */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  BIRA by Status
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {currentProject && (
                  <IssuesStatisticsChart projectId={currentProject.id} chartType="status" />
                )}
              </Paper>
            </Grid>

            {/* Column 3: Statistics by Rank */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  BIRA by Rank
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {currentProject && (
                  <IssuesStatisticsChart projectId={currentProject.id} chartType="rank" />
                )}
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* Issues Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">
                BIRA SUMMARY
                {filterApplied && (
                  <Chip 
                    label="Filtered" 
                    color="primary" 
                    size="small" 
                    sx={{ ml: 1 }} 
                    onDelete={clearAppliedFilter}
                  />
                )}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  mt: 2,
                }}
              >
                {/* Row 1: Sort/Search and Actions */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', lg: 'row' },
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 2,
                  }}
                >
                  {/* Left side: Sort and Search */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2,
                      alignItems: 'flex-start',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <TextField
                      select
                      label="Sort By"
                      variant="outlined"
                      size="small"
                      value={orderBy}
                      onChange={(e) => {
                        setOrderBy(e.target.value as OrderBy);
                      }}
                      sx={{
                        width: 150,
                        '& .MuiSelect-select': {
                          fontWeight: orderBy ? 'bold' : 'normal',
                        },
                      }}
                    >
                      <MenuItem value="id">No</MenuItem>
                      <MenuItem value="type">Type of Issue</MenuItem>
                      <MenuItem value="rank">Rank</MenuItem>
                      <MenuItem value="completion_date">Due Date</MenuItem>
                      <MenuItem value="status">Status</MenuItem>
                      <MenuItem value="progress">Progress</MenuItem>
                    </TextField>
                    <TextField
                      select
                      label="Order"
                      variant="outlined"
                      size="small"
                      value={order}
                      onChange={(e) => {
                        setOrder(e.target.value as Order);
                      }}
                      sx={{
                        width: 150,
                        '& .MuiSelect-select': {
                          fontWeight: order ? 'bold' : 'normal',
                        },
                      }}
                    >
                      <MenuItem value="asc">Ascending</MenuItem>
                      <MenuItem value="desc">Descending</MenuItem>
                    </TextField>
                    
                    {/* Search field - always visible */}
                    <TextField
                      placeholder="Search BIRA..."
                      variant="outlined"
                      size="small"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onBlur={commitSearch}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === 'Tab') {
                          commitSearch();
                        }
                      }}
                      sx={{
                        width: { xs: '100%', sm: 260, md: 300 },
                        '& .MuiInputBase-input': {
                          fontWeight: searchInput ? 'bold' : 'normal',
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  {/* Filter selection info and buttons - only when filter mode is active */}
                  {showFilters && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                        {selectedIssues.length} of {filteredIssues.length} rows selected
                      </Typography>
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={() => setSelectedIssues([])}
                        disabled={selectedIssues.length === 0}
                        sx={{ mr: 1 }}
                      >
                        Clear
                      </Button>
                      <Button 
                        variant="contained" 
                        color="primary"
                        size="small"
                        onClick={applySelectionFilter}
                        disabled={selectedIssues.length === 0 || filterApplied}
                      >
                        Apply Filter
                      </Button>
                    </Box>
                  )}    

                  {/* Right side: Action buttons */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                      alignItems: 'flex-start',
                      alignSelf: 'flex-start',
                    }}
                  >
                    <Tooltip title="Export to Excel">
                      <IconButton 
                        color="primary" 
                        onClick={exportToExcelWithExcelJS}
                      >
                        <FileDownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={showFilters ? "Exit Filter Mode" : "Filter Mode"}>
                      <IconButton
                        color={showFilters ? "secondary" : "default"}
                        onClick={toggleFilters}
                      >
                        <FilterListIcon />
                      </IconButton>
                    </Tooltip>
                    {filterApplied && (
                      <Tooltip title="Clear Applied Filter">
                        <IconButton
                          color="error"
                          onClick={clearAppliedFilter}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Button
                      startIcon={<AddIcon />}
                      variant="contained"
                      onClick={handleCreateIssueClick}
                    >
                      New BIRA
                    </Button>
                  </Box>
                </Box>

                {/* Row 2: Filters */}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.3, fontSize: '1.0rem' }}>
                  Filters :
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: 'flex-start',
                  }}
                >
                  <TextField
                    select
                    label="Status"
                    variant="outlined"
                    size="small"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{ width: { xs: 'calc(50% - 8px)', sm: 100 } }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="25">25%</MenuItem>
                    <MenuItem value="50">50%</MenuItem>
                    <MenuItem value="75">75%</MenuItem>
                    <MenuItem value="100">100%</MenuItem>
                  </TextField>

                  <TextField
                    select
                    label="Rank"
                    variant="outlined"
                    size="small"
                    value={rankFilter}
                    onChange={(e) => setRankFilter(e.target.value)}
                    sx={{ width: { xs: 'calc(50% - 8px)', sm: 100 } }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="a">A</MenuItem>
                    <MenuItem value="b">B</MenuItem>
                    <MenuItem value="c">C</MenuItem>
                  </TextField>

                  <TextField
                    select
                    label="Type"
                    variant="outlined"
                    size="small"
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) => (selected as string[]).join(', '),
                    }}
                    value={issueTypeFilter}
                    onChange={(e) =>
                      setIssueTypeFilter(
                        typeof e.target.value === 'string'
                          ? e.target.value.split(',')
                          : e.target.value
                      )
                    }
                    sx={{ width: { xs: '100%', sm: 120 } }}
                  >
                    {issueTypeOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {formatWord(option)}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    label="Line"
                    variant="outlined"
                    size="small"
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) => (selected as string[]).join(', '),
                    }}
                    value={lineFilter}
                    onChange={(e) =>
                      setLineFilter(
                        typeof e.target.value === 'string'
                          ? e.target.value.split(',')
                          : e.target.value
                      )
                    }
                    sx={{ width: { xs: '100%', sm: 220 } }}
                  >
                    {lineOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    label="Issued By"
                    variant="outlined"
                    size="small"
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) => (selected as string[]).join(', '),
                    }}
                    value={issuedByFilter}
                    onChange={(e) =>
                      setIssuedByFilter(
                        typeof e.target.value === 'string'
                          ? e.target.value.split(',')
                          : e.target.value
                      )
                    }
                    sx={{ width: { xs: '100%', sm: 220 } }}
                  >
                    {issuedByOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    label="CM PIC"
                    variant="outlined"
                    size="small"
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) => (selected as string[]).join(', '),
                    }}
                    value={cmPicFilter}
                    onChange={(e) =>
                      setCmPicFilter(
                        typeof e.target.value === 'string'
                          ? e.target.value.split(',')
                          : e.target.value
                      )
                    }
                    sx={{ width: { xs: '100%', sm: 200 } }}
                  >
                    {cmPicOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    label="Approval"
                    variant="outlined"
                    size="small"
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) => (selected as string[]).join(', '),
                    }}
                    value={approvalFilter}
                    onChange={(e) =>
                      setApprovalFilter(
                        typeof e.target.value === 'string'
                          ? e.target.value.split(',')
                          : e.target.value
                      )
                    }
                    sx={{ width: { xs: '100%', sm: 200 } }}
                  >
                    {approvalOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>

                  {/* Row number filter inputs */}
                  <TextField
                    label="From Row"
                    variant="outlined"
                    size="small"
                    type="number"
                    value={fromRowNumber}
                    onChange={(e) => setFromRowNumber(e.target.value)}
                    inputProps={{ min: '1' }}
                    sx={{ width: { xs: 'calc(50% - 8px)', sm: 140 } }}
                  />
                  <TextField
                    label="To Row"
                    variant="outlined"
                    size="small"
                    type="number"
                    value={toRowNumber}
                    onChange={(e) => setToRowNumber(e.target.value)}
                    inputProps={{ min: '1' }}
                    sx={{ width: { xs: 'calc(50% - 8px)', sm: 140 } }}
                  />
                  {/* Reset button to set to default of sort, search, filter by status and by rank, from row and to row */}
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={resetFilters}
                    sx={{ height: 40 }}
                  >
                    Reset
                  </Button>
                </Box>
              </Box>
            </Box>

            <TableContainer
              sx={{
                maxHeight: '60vh',
                overflowY: 'auto',
                overflowX: 'auto',
                position: 'relative',
              }}
            >
              <Table sx={{ 
                tableLayout: 'auto',
                minWidth: '1400px',
                borderCollapse: 'separate',
                borderSpacing: 0,
              }}>
                <TableHead 
                  sx={{
                    backgroundColor: '#e0e0e0',
                    position: 'sticky',
                    top: 0,
                    // Keep header above sticky left body cells
                    zIndex: 35,
                  }}
                >
                  <TableRow>
                  {/* Apply text color to all header cells */}
                    {showFilters && (
                      <TableCell padding="checkbox" sx={{ ...cellBorder, position: 'sticky', left: 0, zIndex: 11, backgroundColor: '#e0e0e0', width: '50px', minWidth: '50px' }}>
                        <Checkbox
                          indeterminate={selectedIssues.length > 0 && selectedIssues.length < filteredIssues.length}
                          checked={selectAll}
                          onChange={handleSelectAll}
                          
                        />
                      </TableCell>
                    )}
                    <TableCell sx={getStickySx('no', { header: true, width: 60 })}>No</TableCell>
                    <TableCell sx={getStickySx('issueId', { header: true, width: 100 })}>Issue ID</TableCell>
                    <TableCell sx={getStickySx('issueType', { header: true, width: 120 })}>Type of Issue</TableCell>
                    <TableCell sx={getStickySx('rank', { header: true, width: 80 })}>Rank</TableCell>
                    <TableCell sx={getStickySx('line', { header: true, width: 100 })}>Line</TableCell>
                    <TableCell sx={getStickySx('part', { header: true, width: 150 })}>Part No & Name</TableCell>
                    <TableCell sx={getStickySx('problem', { header: true, width: 200, highlightDivider: true })}>Problem Description</TableCell>
                    <TableCell sx={{ ...cellBorder, width: '100px', minWidth: '100px' }}>Picture</TableCell>
                    <TableCell sx={{ ...cellBorder, width: '180px', minWidth: '180px' }}>Counter Measure Request</TableCell>
                    <TableCell sx={{ ...cellBorder, width: '180px', minWidth: '180px' }}>Final Counter Measure</TableCell>
                    <TableCell sx={{ ...cellBorder, width: '150px', minWidth: '150px' }}>Issued By & Date</TableCell>
                    <TableCell sx={{ ...cellBorder, width: '120px', minWidth: '120px' }}>C/M PIC</TableCell>
                    <TableCell sx={{ ...cellBorder, width: '120px', minWidth: '120px' }}>Approver</TableCell>
                    <TableCell sx={{ ...cellBorder, width: '120px', minWidth: '120px' }}>Due Date</TableCell>
                    <TableCell sx={{ ...cellBorder, width: '100px', minWidth: '100px' }}>Status</TableCell>
                    <TableCell sx={{ ...cellBorder, width: '80px', minWidth: '80px' }}>Progress</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {issuesLoading ? (
                    // Show skeleton rows while loading
                    Array.from({ length: rowsPerPage }).map((_, index) => (
                      <TableRow key={`skeleton-${index}`}>
                        {showFilters && <TableCell><Skeleton /></TableCell>}
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                      </TableRow>
                    ))
                  ) : paginatedIssues.map((issue) => (
                    <TableRow 
                      key={`${issue.project_id}-${issue.id}`}
                      hover
                      selected={selectedIssues.includes(issue.id)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      {showFilters && (
                        <TableCell 
                          padding="checkbox" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectRow(issue.id, e);
                          }}
                          sx={{ ...cellBorder, position: 'sticky', left: 0, zIndex: 5, backgroundColor: 'white', width: '50px', minWidth: '50px', overflow: 'hidden', boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)' }}
                        >
                          <Checkbox
                            checked={selectedIssues.includes(issue.id)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleSelectRow(issue.id, e as unknown as React.MouseEvent<HTMLElement>);
                            }}
                          />
                        </TableCell>
                      )}
                      <TableCell
                        sx={{
                          ...getStickySx('no', { width: 60 }),
                          cursor: 'pointer',
                        }}
                        onClick={() => navigateToIssueDetail(issue.id)}
                      >
                        {page * rowsPerPage + paginatedIssues.indexOf(issue) + 1}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...getStickySx('issueId', { width: 100 }),
                          cursor: 'pointer',
                        }}
                        onClick={() => navigateToIssueDetail(issue.id)}
                      >
                        {highlightMatch(issue.issue_code || `${issue.id}`, searchTerm)}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...getStickySx('issueType', { width: 120 }),
                          cursor: 'pointer',
                        }}
                        onClick={() => navigateToIssueDetail(issue.id)}
                      >
                        {highlightMatch(formatWord(issue.type), searchTerm)}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...getStickySx('rank', { width: 80 }),
                          cursor: 'pointer',
                        }}
                        onClick={() => navigateToIssueDetail(issue.id)}
                      >
                        {highlightMatch(formatWord(issue.rank), searchTerm)}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...getStickySx('line', { width: 100 }),
                          cursor: 'pointer',
                        }}
                        onClick={() => navigateToIssueDetail(issue.id)}
                      >
                        {highlightMatch(issue.line_name, searchTerm)}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...getStickySx('part', { width: 150 }),
                          cursor: 'pointer',
                        }}
                        onClick={() => navigateToIssueDetail(issue.id)}
                      >
                        {highlightMatch(issue.part_number, searchTerm)}
                        <br />
                        {highlightMatch(issue.part_name, searchTerm)}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...getStickySx('problem', { width: 200, highlightDivider: true }),
                          cursor: 'pointer',
                        }}
                        onClick={() => navigateToIssueDetail(issue.id)}
                      >
                        {highlightMatch(issue.problem, searchTerm)}
                      </TableCell>
                      <TableCell sx={{ ...cellBorder, width: '100px', minWidth: '100px' }} onClick={() => navigateToIssueDetail(issue.id)}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {issue.photos?.slice(0, 2).map((photo) => (
                            <Box
                              key={photo.id}
                              component="img"
                              src={loadedImages[`${photo.id}`] || getImageUrl(photo.file_name)}
                              alt={`Issue photo ${photo.id}`}
                              sx={{
                                width: 40,
                                height: 40,
                                objectFit: 'cover',
                                borderRadius: 1,
                                cursor: 'pointer',
                              }}
                              onError={(e) => {
                                // Fallback to placeholder if image fails to load
                                const target = e.target as HTMLImageElement;
                                target.onerror = null; // Prevent infinite loop
                                target.src = '/placeholder-image.svg';
                              }}
                              onClick={() => setSelectedImage(loadedImages[`${photo.id}`] || getImageUrl(photo.file_name))}
                            />
                          ))}
                          {issue.photos && issue.photos.length > 2 && (
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'grey.200',
                                borderRadius: 1,
                                typography: 'caption',
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                // Show first photo when clicking +N
                                if (issue.photos && issue.photos.length > 0) {
                                  setSelectedImage(getImageUrl(issue.photos[0].file_name));
                                }
                              }}
                            >
                              +{issue.photos.length - 2}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...cellBorder, width: '180px', minWidth: '180px' }} onClick={() => navigateToIssueDetail(issue.id)}>{highlightMatch(issue.counter_measure, searchTerm)}</TableCell>
                      <TableCell sx={{ ...cellBorder, width: '180px', minWidth: '180px' }} onClick={() => navigateToIssueDetail(issue.id)}>{highlightMatch(issue.final_counter_measure, searchTerm)}</TableCell>
                      <TableCell sx={{ ...cellBorder, width: '150px', minWidth: '150px' }} onClick={() => navigateToIssueDetail(issue.id)}>
                        {highlightMatch(issue.created_by_name, searchTerm)}
                        <br />
                        {formatDate(new Date(issue.created_on))}
                      </TableCell>
                      <TableCell sx={{ ...cellBorder, width: '120px', minWidth: '120px' }} onClick={() => navigateToIssueDetail(issue.id)}>{highlightMatch(issue.pic_name || "", searchTerm)}</TableCell>
                      <TableCell sx={{ ...cellBorder, width: '120px', minWidth: '120px' }} onClick={() => navigateToIssueDetail(issue.id)}>{highlightMatch(issue.approver_pic_name || "", searchTerm)}</TableCell>
                      <TableCell sx={{ ...cellBorder, width: '120px', minWidth: '120px' }} onClick={() => navigateToIssueDetail(issue.id)}>{issue.completion_date ? formatDate(new Date(issue.completion_date)) : ""}</TableCell>
                      <TableCell sx={{ ...cellBorder, width: '100px', minWidth: '100px' }} onClick={() => navigateToIssueDetail(issue.id)}>
                        {highlightMatch(formatWord(issue.status), searchTerm)}
                      </TableCell>
                      <TableCell sx={{ ...cellBorder, width: '80px', minWidth: '80px' }} align="center" onClick={() => navigateToIssueDetail(issue.id)}>
                        <Box sx={{ position: 'relative', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {(() => {
                            const percentage = getStatusPercentage(issue.status);
                            let imageName = 'progress-25.png';
                            
                            if (percentage <= 0) {
                              // For 0% progress, just show the text without an image
                              return (
                                <Box 
                                  sx={{ 
                                    position: 'relative', 
                                    width: '40px', 
                                    height: '40px',
                                    border: '1px solid #ccc',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '10px',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    0%
                                  </Typography>
                                </Box>
                              );
                            } else if (percentage <= 0.25) {
                              imageName = 'progress-25.png';
                            } else if (percentage <= 0.5) {
                              imageName = 'progress-50.png';
                            } else if (percentage <= 0.75) {
                              imageName = 'progress-75.png';
                            } else {
                              imageName = 'progress-100.png';
                            }
                            
                            return (
                              <Box sx={{ position: 'relative', width: '40px', height: '40px' }}>
                                <img 
                                  src={`${process.env.PUBLIC_URL}/${imageName}`} 
                                  alt={`${Math.round(percentage * 100)}% complete`}
                                  style={{ width: '100%', height: '100%' }}
                                />
                              </Box>
                            );
                          })()}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!issuesLoading && filteredIssues.length === 0 && (
                    <TableRow>
                      <TableCell sx={cellBorder} colSpan={showFilters ? 17 : 16} align="center">
                        No issues found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredIssues.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* QR Code Share Dialog */}
      <Dialog 
        open={shareDialogOpen} 
        onClose={() => setShareDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Share Project</Typography>
            <IconButton edge="end" onClick={() => setShareDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            p={2}
          >
            <Typography variant="body1" gutterBottom>
              Scan this QR code to open on mobile device
            </Typography>
            <Box 
              sx={{ 
                mt: 2, 
                mb: 3, 
                p: 2, 
                border: '1px solid #eee', 
                borderRadius: 1,
                backgroundColor: 'white' 
              }}
            >
              <QrCode value={getPublicUrl()} size={200} />
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              value={getPublicUrl()}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Copy to clipboard">
                      <IconButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyUrl();
                        }} 
                        edge="end"
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Dialogs */}
      <ProjectFormDialog
        open={projectFormOpen}
        onClose={() => setProjectFormOpen(false)}
        onSubmit={handleProjectFormSubmit}
        project={currentProject}
        onSuccess={(message) => setSuccessMessage(message)}
      />

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Image Dialog */}
      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {selectedImage && (
            <>
              <IconButton
                onClick={() => setSelectedImage(null)}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  bgcolor: 'background.paper',
                  '&:hover': {
                    bgcolor: 'background.paper',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
              <Box
                component="img"
                src={selectedImage}
                alt="Full size"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
